import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useClientAccessCache } from '../../api/useClientAccessQuery';
import {
  hasDataAggregationSelector,
  hasDataReconciliationSelector,
  selectHasBeFi20,
} from '../../../AdviserArea/AdviserAreaSelector';
import {
  ClientAccessData,
  ClientAccessSettingsState,
  LandingPage as LandingPageEnum,
  SaveParams,
} from '../../ClientAccess.types';
import { getIsLandingPageValid, getIsTogglingDashboard } from '../LandingPage/useLandingPage';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { landingPageNameMap } from '../LandingPage/LandingPage.meta';
import { ClientAccessSettings } from '../ClientAccessSettings/ClientAccessSettings';
import { Separator } from '../../../CommonComponents/OPL/Separator/Separator';
import { LandingPage } from '../LandingPage/LandingPage';
import { IntegratedFrom } from '../IntegratedFrom/IntegratedFrom';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { SaveAndPreview } from '../SaveAndPreview/SaveAndPreview';
import { OnboardingWorkflow } from '../../../CommonSelectors/CommonArea.types';
import { isOrionClientSelector } from '../../../CommonSelectors/CommonAreaSelector';

export interface Props {
  clientId: string;
  onClose(): void;
  data: ClientAccessData;
  onSave(params: SaveParams): Promise<void>;
  onNext?(): void;
  isClientRegistered: boolean;
  isSavingClientAccess: boolean;
  defaultOnboardingWorkflow?: OnboardingWorkflow;
}

export const ConfigureAccess = ({
  clientId,
  data,
  onSave,
  onNext,
  onClose,
  isClientRegistered,
  isSavingClientAccess,
  defaultOnboardingWorkflow,
}: Props) => {
  const [isSavingAndPreviewing, setIsSavingAndPreviewing] = useState(false);

  const [isLandingPageAlert, setIsLandingPageAlert] = useState(false);

  const { mutateClientAccess } = useClientAccessCache({ clientId, defaultOnboardingWorkflow });

  const showDataAggregation = useSelector(hasDataAggregationSelector);
  const showDataReconciliation = useSelector(hasDataReconciliationSelector);
  const hasBeFi20 = useSelector(selectHasBeFi20);
  const isOrionClient = useSelector(isOrionClientSelector);

  const onChange = useCallback(
    async (newData: Partial<ClientAccessSettingsState>) => {
      const mergedData = {
        ...data,
        ...newData,
      };

      const isTogglingDashboard = getIsTogglingDashboard({
        oldDashboardAccess: data.dashboardAccess,
        newDashBoardAccess: newData.dashboardAccess,
      });

      const isLandingPageValid = getIsLandingPageValid({
        clientLandingPage: mergedData.clientLandingPage,
        dashboardAccess: mergedData.dashboardAccess,
        landingPageOptions: data.clientLandingPageOptions,
        hasOrionReportsEnabled: data.hasOrionReportsEnabled,
        isOrionClient,
      });

      if (!isLandingPageValid && !isTogglingDashboard) {
        setIsLandingPageAlert(true);
      } else {
        setIsLandingPageAlert(false);
      }

      mutateClientAccess({
        ...mergedData,
        clientLandingPage: !isLandingPageValid
          ? isTogglingDashboard
            ? null
            : LandingPageEnum.Overview
          : mergedData.clientLandingPage,
      });
    },
    [data, isOrionClient, mutateClientAccess],
  );

  const foundWorkflowOption = data.onboardingWorkflowOptions?.find(
    ({ value }) => value === data.onboardingWorkflow,
  );

  const onboardingWorkflow = foundWorkflowOption ? foundWorkflowOption.value : null;

  return (
    <>
      <ModalBody css="overflow-y: scroll; height: 640px;">
        {isLandingPageAlert && (
          <Alert
            message={`Changing this setting will now default the client portal landing page to ${
              landingPageNameMap[LandingPageEnum.Overview]
            }`}
            variant="warning"
            onRequestClose={() => setIsLandingPageAlert(false)}
            my="16"
          />
        )}

        <ClientAccessSettings
          {...data}
          clientId={clientId}
          showBeFi20={hasBeFi20}
          showDataAggregation={showDataAggregation}
          showDataReconciliation={showDataReconciliation}
          onboardingWorkflow={onboardingWorkflow}
          onChange={onChange}
        />

        <Separator />

        <LandingPage {...data} onChange={onChange} my="16" />

        <IntegratedFrom clientId={clientId} my="16" />
      </ModalBody>

      <ModalFooter hasBorder>
        <Button variant="subtleInactive" onClick={onClose} mr="auto">
          Cancel
        </Button>

        <SaveAndPreview
          onClick={async () => {
            setIsSavingAndPreviewing(true);

            await onSave({
              isSaveAndPreview: true,
              onSuccess() {
                window.open(`/Consumer/Consumer?clientId=${clientId}&cx=true`, '_blank');
                onClose();
              },
            });

            setIsSavingAndPreviewing(false);
          }}
          isLoading={isSavingAndPreviewing}
          {...data}
        />

        {isClientRegistered ? (
          <Button
            isLoading={isSavingClientAccess && !isSavingAndPreviewing}
            onClick={() => onSave({ onSuccess: onClose })}
            ml="8"
          >
            Save
          </Button>
        ) : (
          <Button
            onClick={() =>
              onSave({
                onSuccess() {
                  if (onNext) {
                    onNext();
                  }
                },
              })
            }
            isLoading={isSavingClientAccess && !isSavingAndPreviewing}
            rightIcon="fas fa-chevron-right"
            ml="8"
          >
            Save & Next
          </Button>
        )}
      </ModalFooter>
    </>
  );
};
